import {
  useCallback, useEffect, useMemo, useRef, useState,
} from 'react';
import {
  Table,
  ModelSelect,
  SecondaryButton,
  CloseInfo,
  Flex,
  useDeleteAlert,
  ModelNewNumericInput,
  flattenErrorTypeArray,
  IconButton,
  Tooltip,
} from '@equifinance/shared';

import styled from 'styled-components';
import { StaticDataItem } from '../../../../api/useAccountApi';
import { TableStatusIcon } from '../../Icons/TableStatusIcon';
import { PlusIcon, RowDeleteIcon } from '../../components/Icons';
import { SectionTitleIcon } from '../../Icons/SectionTitleIcon';
import {
  AddressType, RecordType, RentalIncomeAndExpenditures, SourceType, useAccountContext,
} from '../../../../contexts';
import RentalRIExpandable from './RentalRIExpandable';
import useErrorBasedRowStyles from '../../../../hooks/useErrorBasedRowStyles';
import { PaddedSection } from '../../components/Section';
import { useProgressStatusByInitiator } from '../../../../contexts/progressStatus/ProgressStatusProvider';

export const RentalIncomeExpenditureRefName = 'rental-income-expenditure';

const ButtonContainer = styled(Flex)`
  justify-content:end;
`;

const AddressContainer = styled.div`
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
`;

function AddressCell({ rentalRI }: { rentalRI: RentalIncomeAndExpenditures }) {
  const addressRef = useRef<HTMLDivElement>(null);
  const [isTruncated, setIsTruncated] = useState(false);

  useEffect(() => {
    const isTextTruncated = (addressRef?.current?.scrollHeight || 0) > (addressRef.current?.clientHeight || 0);
    setIsTruncated(isTextTruncated);
  }, [rentalRI]);

  const address = useMemo(() => (rentalRI?.address === null || (!rentalRI?.address?.houseNumber && !rentalRI?.address?.houseName && !rentalRI?.address?.street && !rentalRI?.address?.postCode)
    ? 'Address will be displayed here'
    : `${rentalRI?.address?.houseNumber || rentalRI?.address?.houseName || ''} ${rentalRI?.address?.street || ''} ${rentalRI?.address?.postCode || ''}`), [rentalRI]);

  return (
    <>
      <AddressContainer ref={addressRef} id={`rentalRI-address-${rentalRI.id}`}>
        {address}
      </AddressContainer>
      {isTruncated && (
        <Tooltip
          id={`rentalRI-address-${rentalRI.id}`}
          html={address}
          style={{ position: 'absolute' }}
        />
      )}
    </>
  );
}

export default function RentalIncomeExpenditure({
  countries,
}: {
  countries: StaticDataItem[],
}) {
  const {
    account,
    updateAccount,
    deleteFromAccountArray,
    appendToAccountArray,
  } = useAccountContext();

  const {
    errors,
    warnings,
    rentalIncomeAndExpenditures,
  } = account;

  const conditionalRowStyles = useErrorBasedRowStyles(errors, warnings);

  const addedRentalIEs = useMemo(() => rentalIncomeAndExpenditures?.filter((rentalIE) => rentalIE?.isAddedToTable), [rentalIncomeAndExpenditures]);

  const rentalIEIndices = useMemo(() => rentalIncomeAndExpenditures?.reduce((prev, current, index) => ({
    ...prev,
    [current.id]: index,
  }), {} as Record<string, number>), [rentalIncomeAndExpenditures]);

  const Status = useCallback((RentalIncomeExpenditure: RentalIncomeAndExpenditures) => (
    <TableStatusIcon
      recordErrors={flattenErrorTypeArray(errors[RentalIncomeExpenditure?.id]) ?? []}
      recordWarnings={flattenErrorTypeArray(warnings[RentalIncomeExpenditure?.id]) ?? []}
      errors={
        flattenErrorTypeArray([
          ...(errors[RentalIncomeExpenditure?.id] ?? []),
          ...(errors[RentalIncomeExpenditure?.address?.id] ?? []),
        ])
      }
      showDefault={!RentalIncomeExpenditure?.id}
    />
  ), [addedRentalIEs]);

  const AddressTypeSelect = useCallback((RentalIncomeExpenditure: RentalIncomeAndExpenditures) => (
    <ModelSelect
      options={AddressType}
      field={`rentalIncomeAndExpenditures[${rentalIEIndices[RentalIncomeExpenditure?.id]}].addressType`}
      required
      isTooltipError
    />
  ), [addedRentalIEs]);

  const RentalIncome = useCallback((RentalIncomeExpenditure: RentalIncomeAndExpenditures) => (
    <>
      {RentalIncomeExpenditure?.addressType !== 'SecondProperty' ? (
        <ModelNewNumericInput
          field={`rentalIncomeAndExpenditures[${rentalIEIndices[RentalIncomeExpenditure?.id]}].rentalIncomePerMonth`}
          required
          disabled={!RentalIncomeExpenditure?.addressType}
          prefix="£"
          isFixedDecimal
          maxCharacters={10}
          isTooltipError
        />
      ) : <>N/A</>}
    </>
  ), [addedRentalIEs]);

  const Expenditure = useCallback((RentalIncomeExpenditure: RentalIncomeAndExpenditures) => (
    <>
      {RentalIncomeExpenditure?.addressType !== 'BuyToLet' ? (
        <ModelNewNumericInput
          field={`rentalIncomeAndExpenditures[${rentalIEIndices[RentalIncomeExpenditure?.id]}].expenditurePerMonth`}
          required
          prefix="£"
          isFixedDecimal
          maxCharacters={10}
          disabled={!RentalIncomeExpenditure?.addressType}
          isTooltipError
        />
      ) : <>N/A</>}
    </>
  ), [addedRentalIEs]);

  const MortgagePayment = useCallback((RentalIncomeExpenditure: RentalIncomeAndExpenditures) => (
    <ModelNewNumericInput
      field={`rentalIncomeAndExpenditures[${rentalIEIndices[RentalIncomeExpenditure?.id]}].mortgagePaymentPerMonth`}
      required
      disabled={!RentalIncomeExpenditure?.addressType}
      prefix="£"
      isFixedDecimal
      maxCharacters={10}
      isTooltipError
    />
  ), [addedRentalIEs]);

  const deleteAlert = useDeleteAlert();

  const showDeleteAlert = (RentalIncomeExpenditure: RentalIncomeAndExpenditures) => {
    deleteAlert({
      onConfirm: () => deleteFromAccountArray('rentalIncomeAndExpenditures', RentalIncomeExpenditure?.id),
      text: 'Are you sure you want to delete this property?',
      content: 'This action cannot be undone.',
    });
  };

  const Delete = useCallback((RentalIncomeExpenditure: RentalIncomeAndExpenditures) => (
    <>
      {RentalIncomeExpenditure?.canBeDeleted && (
        <IconButton onClick={() => showDeleteAlert(RentalIncomeExpenditure)}>
          <RowDeleteIcon />
        </IconButton>
      )}
    </>
  ), [addedRentalIEs]);

  const columns = [
    {
      name: 'Status',
      sortable: true,
      cell: (RentalIncomeExpenditure: RentalIncomeAndExpenditures) => Status(RentalIncomeExpenditure),
      width: '70px',
    },
    {
      name: 'Address',
      sortable: true,
      cell: (rentalRI: RentalIncomeAndExpenditures) => AddressCell({ rentalRI }),
      width: '310px',
    },
    {
      name: 'Source',
      sortable: true,
      selector: (RentalIncomeExpenditure: RentalIncomeAndExpenditures) => (RentalIncomeExpenditure?.recordType),
      width: '75px',
    },
    {
      name: 'Type',
      sortable: true,
      cell: (RentalIncomeExpenditure: RentalIncomeAndExpenditures) => (AddressTypeSelect(RentalIncomeExpenditure)),
    },
    {
      name: 'Rental income per month (£)',
      sortable: true,
      cell: (RentalIncomeExpenditure: RentalIncomeAndExpenditures) => RentalIncome(RentalIncomeExpenditure),
    },
    {
      name: 'Expenditure per month (£)',
      sortable: true,
      cell: (RentalIncomeExpenditure: RentalIncomeAndExpenditures) => Expenditure(RentalIncomeExpenditure),
    },
    {
      name: 'Mortgage payment per month (£)',
      sortable: true,
      cell: (RentalIncomeExpenditure: RentalIncomeAndExpenditures) => MortgagePayment(RentalIncomeExpenditure),
    },
    {
      name: '',
      sortable: true,
      cell: (RentalIncomeExpenditure: RentalIncomeAndExpenditures) => Delete(RentalIncomeExpenditure),
      width: '30px',
    },
  ];

  const mergedErrors = flattenErrorTypeArray(rentalIncomeAndExpenditures?.map((rentalIncomeExpenditure) => (
    [
      ...(errors[rentalIncomeExpenditure?.id] ?? []),
      ...(errors[rentalIncomeExpenditure?.address?.id] ?? []),
    ]
  )).flat().filter((e) => !!e));

  const addNewPropertyInProgress = useProgressStatusByInitiator('addNewProperty-button');

  const [expandedRows, setExpandedRows] = useState<string[]>([]);
  const [currentRIERows, setCurrentRIERows] = useState<string[]>([]);
  const prevRIERowsRef = useRef<string[]>([]);
  const isFirstAddNewRowRef = useRef<boolean>(false);

  useEffect(() => {
    const currentRows = addedRentalIEs.map((rie) => rie?.id ?? '');
    setCurrentRIERows([...currentRows]);
    if (!isFirstAddNewRowRef.current) {
      prevRIERowsRef.current = [...currentRows];
    }
  }, [addedRentalIEs]);

  useEffect(() => {
    if (!isFirstAddNewRowRef.current) return;
    const prevItems = prevRIERowsRef.current;
    const addedItems = currentRIERows.filter((item) => !prevItems.includes(item));
    setExpandedRows(addedItems);
    prevRIERowsRef.current = [...currentRIERows];
  }, [currentRIERows, isFirstAddNewRowRef.current]);

  const addNewProperty = () => {
    if (!isFirstAddNewRowRef.current) {
      isFirstAddNewRowRef.current = true;
    }

    appendToAccountArray('rentalIncomeAndExpenditures', {
      recordType: RecordType.Declared,
      source: SourceType.Equifax,
      isAddedToTable: true,
      canBeDeleted: true,
    } as RentalIncomeAndExpenditures, 'addNewProperty-button');
  };

  return (
    <PaddedSection
      id={RentalIncomeExpenditureRefName}
      refName={RentalIncomeExpenditureRefName}
      title="Rental Income and Expenditure"
      titleIcon={<SectionTitleIcon errors={mergedErrors} />}
    >
      <CloseInfo>If any of the Applicants have additional properties, please add them to the table below.</CloseInfo>
      <ButtonContainer>
        <SecondaryButton onClick={addNewProperty} disabled={addNewPropertyInProgress} small>
          <PlusIcon />
          Add new property
        </SecondaryButton>
      </ButtonContainer>
      <Table
        columns={columns}
        forcedExpandRows={expandedRows}
        data={addedRentalIEs ?? []}
        showSearch={false}
        expandableRowsComponent={RentalRIExpandable}
        expandableRowDisabled={(rentalIncomeExpenditure: RentalIncomeAndExpenditures) => rentalIncomeExpenditure?.recordType === RecordType.Equifax}
        expandableRowsComponentProps={{
          'update': updateAccount,
          rentalIEIndices,
          countries,
          errors,
          warnings,
        }}
        conditionalRowStyles={conditionalRowStyles}
      />
    </PaddedSection>
  );
}
